<template>
  <header ref="siteHeader" class="site-header" @keydown.esc="dismissMenu">
    <div class="header-inner brand-container">
      <router-link
        to="/"
        class="logo"
        aria-label="Mola Games home"
        @click="closeMenu"
      >
        <img src="/brand/logo.svg" alt="Mola Games" width="144" height="50" />
      </router-link>

      <div class="nav-wrapper">
        <nav ref="desktopNav" class="desktop-nav" aria-label="Main navigation">
          <router-link
            v-for="item in navigation"
            :key="item.label"
            :to="item.to"
            custom
            v-slot="{ href, navigate }"
          >
            <a
              :href="href"
              @click="navigate"
              :class="{ 'is-active': isNavActive(item) }"
              :aria-current="isNavActive(item) ? item.current : null"
              >{{ item.label }}</a
            >
          </router-link>
          <span
            v-if="navIndicator.width > 0 && navIndicator.visible"
            class="nav-selection"
            aria-hidden="true"
            :style="{
              width: `${navIndicator.width}px`,
              transform: `translateX(${navIndicator.left}px)`
            }"
          ></span>
        </nav>
        <!-- Previous navigation and wallet entry point, retained for future use.
        <nav class="desktop-nav" aria-label="Main navigation">
          <router-link to="/">Home</router-link>
          <router-link to="/leaderboard">Leaderboard</router-link>
          <router-link to="/privacy-policy">Privacy Policy</router-link>
        </nav>
        <router-link to="/wallet" class="wallet-link">
          {{ isAuthenticated ? shortAddress : 'Connect Wallet' }}
          <span aria-hidden="true">↗</span>
        </router-link>
        -->
      </div>

      <button
        ref="menuButton"
        type="button"
        class="hamburger"
        @click="isMenuOpen = !isMenuOpen"
        :aria-label="
          isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'
        "
        :aria-expanded="isMenuOpen"
        aria-controls="mobile-nav"
      >
        <span :class="{ open: isMenuOpen }"></span>
        <span :class="{ open: isMenuOpen }"></span>
      </button>
    </div>

    <Transition name="mobile-menu">
      <nav
        v-show="isMenuOpen"
        id="mobile-nav"
        class="mobile-nav"
        aria-label="Mobile navigation"
        :inert="!isMenuOpen ? '' : null"
      >
        <router-link
          v-for="item in navigation"
          :key="item.label"
          :to="item.to"
          custom
          v-slot="{ href, navigate }"
        >
          <a
            :href="href"
            @click="
              closeMenu();
              navigate($event);
            "
            :class="{ 'is-active': isNavActive(item) }"
            :aria-current="isNavActive(item) ? item.current : null"
            >{{ item.label }}</a
          >
        </router-link>
        <!-- Previous mobile navigation and wallet entry point, retained for future use.
      <router-link to="/" @click="closeMenu">Home</router-link>
      <router-link to="/leaderboard" @click="closeMenu"
        >Leaderboard</router-link
      >
      <router-link to="/privacy-policy" @click="closeMenu"
        >Privacy Policy</router-link
      >
      <router-link to="/wallet" @click="closeMenu" class="mobile-wallet-link">
        {{ isAuthenticated ? shortAddress : 'Connect Wallet' }}
        <span aria-hidden="true">↗</span>
      </router-link>
      --></nav>
    </Transition>
  </header>
</template>

<script>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
// import { useZkAuth } from '../services/zkAuth';

export default {
  name: 'AppHeader',
  setup() {
    const isMenuOpen = ref(false);
    const siteHeader = ref(null);
    const menuButton = ref(null);
    const desktopNav = ref(null);
    const navIndicator = ref({ left: 0, width: 0, visible: false });
    let navResizeObserver;
    let desktopQuery;
    const route = useRoute();
    // const { isAuthenticated, shortAddress } = useZkAuth();
    const navigation = [
      { label: 'Home', to: '/', path: '/', hash: '', current: 'page' },
      {
        label: 'Games',
        to: '/#games',
        path: '/',
        hash: '#games',
        current: 'location'
      },
      { label: 'Career', to: '/career', path: '/career', current: 'page' }
    ];
    const isNavActive = (item) =>
      (route.path === item.path ||
        (item.path === '/career' && route.path.startsWith('/career/'))) &&
      (item.hash === undefined || route.hash === item.hash);
    const closeMenu = () => {
      isMenuOpen.value = false;
    };
    const dismissMenu = () => {
      if (!isMenuOpen.value) return;
      closeMenu();
      menuButton.value?.focus();
    };
    const closeOnOutsidePress = (event) => {
      if (isMenuOpen.value && !siteHeader.value?.contains(event.target))
        closeMenu();
    };
    const closeOnDesktop = (event) => {
      if (event.matches) closeMenu();
    };
    const updateNavIndicator = () => {
      const nav = desktopNav.value;
      if (!nav?.offsetWidth) return;
      const activeLink = nav.querySelector('a.is-active');
      if (!activeLink) {
        navIndicator.value = { ...navIndicator.value, visible: false };
        return;
      }
      navIndicator.value = {
        left: activeLink.offsetLeft,
        width: activeLink.offsetWidth,
        visible: true
      };
    };
    watch(() => route.fullPath, closeMenu);
    watch(() => route.fullPath, updateNavIndicator, { flush: 'post' });
    onMounted(() => {
      updateNavIndicator();
      navResizeObserver = new ResizeObserver(updateNavIndicator);
      navResizeObserver.observe(desktopNav.value);
      document.addEventListener('pointerdown', closeOnOutsidePress);
      desktopQuery = window.matchMedia('(min-width: 901px)');
      desktopQuery.addEventListener('change', closeOnDesktop);
    });
    onBeforeUnmount(() => {
      navResizeObserver?.disconnect();
      document.removeEventListener('pointerdown', closeOnOutsidePress);
      desktopQuery?.removeEventListener('change', closeOnDesktop);
    });
    return {
      isMenuOpen,
      siteHeader,
      menuButton,
      desktopNav,
      navIndicator,
      closeMenu,
      dismissMenu,
      navigation,
      isNavActive
      // isAuthenticated,
      // shortAddress
    };
  }
};
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: var(--mola-ivory);
  border-bottom: 1px solid var(--brand-border);
  padding-top: env(safe-area-inset-top, 0px);
}
.header-inner {
  min-height: 96px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}
.logo {
  display: block;
  flex-shrink: 0;
}
.logo img {
  display: block;
  width: 144px;
  height: auto;
}
.nav-wrapper,
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 32px;
}
.desktop-nav a {
  color: var(--mola-navy);
  font-size: 0.86rem;
  font-weight: 600;
  text-decoration: none;
  padding-block: 12px;
  transition: color var(--brand-feedback-duration) var(--brand-feedback-easing);
}
.desktop-nav a:hover,
.desktop-nav a.is-active {
  color: var(--mola-blue);
}
.desktop-nav {
  position: relative;
}
.nav-selection {
  position: absolute;
  bottom: 4px;
  left: 0;
  height: 2px;
  border-radius: 2px;
  background: var(--mola-blue);
  pointer-events: none;
  transition: transform var(--brand-navigation-duration)
      var(--brand-slide-easing),
    width var(--brand-navigation-duration) var(--brand-slide-easing);
}
.wallet-link {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 13px 22px;
  border: 1px solid var(--mola-blue);
  border-radius: 999px;
  color: var(--mola-blue);
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 700;
  transition: background-color var(--brand-feedback-duration)
      var(--brand-feedback-easing),
    color var(--brand-feedback-duration) var(--brand-feedback-easing);
}
.wallet-link:hover {
  background: var(--mola-blue);
  color: var(--mola-ivory);
}
.wallet-link span {
  font-size: 1.15rem;
}
.hamburger {
  display: none;
  width: 46px;
  height: 46px;
  border: 1px solid var(--brand-border);
  border-radius: 50%;
  background: transparent;
  color: var(--mola-blue);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
}
.hamburger span {
  width: 18px;
  height: 2px;
  background: currentColor;
  transition: transform var(--brand-hover-duration) var(--brand-slide-easing);
}
.hamburger span.open:first-child {
  transform: translateY(4px) rotate(45deg);
}
.hamburger span.open:last-child {
  transform: translateY(-4px) rotate(-45deg);
}
.mobile-nav {
  display: none;
}
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity var(--brand-hover-duration) var(--brand-feedback-easing),
    transform var(--brand-hover-duration) var(--brand-slide-easing);
  transform-origin: top;
}
.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
@media (max-width: 900px) {
  .header-inner {
    min-height: 80px;
  }
  .logo img {
    width: 128px;
  }
  .nav-wrapper {
    display: none;
  }
  .hamburger {
    display: flex;
  }
  .mobile-nav {
    position: absolute;
    inset: 100% 0 auto;
    background: var(--mola-ivory);
    box-shadow: 0 18px 28px rgb(4 30 66 / 8%);
    display: flex;
    flex-direction: column;
    padding: 8px 20px 24px;
    border-top: 1px solid var(--brand-border);
  }
  .mobile-nav a {
    padding: 16px 12px;
    color: var(--mola-navy);
    text-decoration: none;
    font-weight: 600;
    border-bottom: 1px solid var(--brand-border);
    transition: color var(--brand-feedback-duration)
        var(--brand-feedback-easing),
      background-color var(--brand-feedback-duration)
        var(--brand-feedback-easing);
  }
  .mobile-nav a.is-active {
    color: var(--mola-blue);
  }
  .mobile-nav a.mobile-wallet-link {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
    border-radius: 12px;
    background: var(--mola-blue);
    color: var(--mola-ivory);
  }
}
</style>
